<script setup>
  import { nextTick, watch, useTemplateRef } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Icon from '@/components/icons/Icon.vue';
  import ScrollAffordance from '@/components/ScrollAffordance.vue';
  import WidgetPreview from '@/components/WidgetPreview.vue';
  import WidgetEmbedFrame from '@/components/WidgetEmbedFrame.vue';
  import { useWidgetStore } from '@/stores/WidgetStore';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';

  const widgetStore = useWidgetStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const props = defineProps({
    // 'cards': the wide editor's sidebar, a card with a live thumbnail per widget
    // 'row': the stacked editor's scrolling row of widget buttons
    layout: {
      type: String,
      default: 'row'
    }
  });

  const { t } = useI18n();

  // The gallery's ScrollAffordance (its nav)
  const gallery = useTemplateRef('gallery');

  function getTitle(widgetId) {
    return t(widgetStore.getDefinition(widgetId).titleKey);
  }

  function isActive(widgetId) {
    return widgetStore.activeWidgetId === widgetId;
  }

  // Bring the widget being customized into view in the gallery's own scroll (the sidebar
  // scrolls down, the row scrolls across), without scrolling anything else
  function scrollActiveIntoView() {
    const list = gallery.value?.$el.querySelector('.jwx-widget-editor-gallery__cards, .jwx-widget-editor-gallery__row');
    const activeItem = list?.querySelector('[data-active]');

    if (!activeItem) return;

    if (props.layout === 'row') {
      list.scrollLeft = activeItem.offsetLeft - (list.clientWidth - activeItem.offsetWidth) / 2;
      return;
    }

    // Offsets include the list's top padding, so the first card scrolls all the way to the top
    const top = activeItem.offsetTop - parseFloat(getComputedStyle(list).paddingTop);

    if (top < list.scrollTop || activeItem.offsetTop + activeItem.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = Math.max(top, 0);
    }
  }

  // A card's button is disabled once its widget is being edited, which would drop focus;
  // send it to that card's title instead
  async function handleSelect(widgetId, event) {
    const hadFocus = event?.currentTarget === document.activeElement;

    // The row's buttons stay enabled; picking the widget already being edited changes nothing
    if (isActive(widgetId)) return;

    widgetStore.customize(widgetId);

    googleAnalyticsStore.sendEvent('Web_Widget_Customizer_Opened', {
      Spot_Name: widgetStore.analyticsSpotName,
      Widget: widgetStore.getDefinition(widgetId).analyticsName,
      Location: 'Customizer'
    });

    if (!hadFocus) return;

    await nextTick();
    gallery.value?.$el.querySelector('.jwx-widget-editor-gallery__card[data-active] .jwx-widget-editor-gallery__card-title')?.focus();
  }

  watch(() => [widgetStore.activeWidgetId, props.layout], async () => {
    await nextTick();
    scrollActiveIntoView();
  }, { immediate: true });
</script>

<template>
  <!-- The nav is the ScrollAffordance, which holds the scroll buttons over the list -->
  <ScrollAffordance
    v-if="props.layout === 'cards'"
    ref="gallery"
    v-slot="{ scrollerRef, fadeEdges }"
    tag="nav"
    axis="vertical"
    class="jwx-widget-editor__gallery jwx-widget-editor-gallery"
    data-layout="cards"
    :aria-label="t('widgets.editorGallery')"
    :previousLabel="t('widgets.showPreviousWidgets')"
    :nextLabel="t('widgets.showMoreWidgets')"
  >
    <ul :ref="scrollerRef" class="jwx-widget-editor-gallery__cards" :data-fade="fadeEdges">
      <li
        v-for="widgetId of widgetStore.availableWidgets"
        :key="widgetId"
        class="jwx-widget-editor-gallery__card"
        :data-active="isActive(widgetId) || null"
        :aria-current="isActive(widgetId) || null"
      >
        <h3 class="jwx-widget-editor-gallery__card-title" tabindex="-1">{{ getTitle(widgetId) }}</h3>
        <div class="jwx-widget-editor-gallery__thumbnail">
          <WidgetPreview :width="widgetStore.embeds[widgetId].width" :height="widgetStore.embeds[widgetId].height">
            <WidgetEmbedFrame :widgetId="widgetId" />
          </WidgetPreview>
        </div>
        <!-- While its widget is being edited the button is disabled and reads "Editing";
             switching widgets slides the label text, not the button -->
        <button
          @click="handleSelect(widgetId, $event)"
          type="button"
          class="jwx-button-control jwx-widget-editor-gallery__card-button"
          data-surface="1"
          data-shape="pill"
          :disabled="isActive(widgetId)"
        >
          <Transition name="jwx-widget-editor-gallery__card-button-label">
            <span v-if="isActive(widgetId)" class="jwx-widget-editor-gallery__card-button-label" data-label="editing">
              <Icon name="edit" class="jwx-button-control__icon" />
              {{ t('widgets.editing') }}
              <span class="sr-only">{{ getTitle(widgetId) }}</span>
            </span>
            <span v-else class="jwx-widget-editor-gallery__card-button-label" data-label="customize">
              <Icon name="sliders" class="jwx-button-control__icon" />
              {{ t('widgets.customize') }}
              <span class="sr-only">{{ getTitle(widgetId) }}</span>
            </span>
          </Transition>
        </button>
      </li>
    </ul>
  </ScrollAffordance>

  <ScrollAffordance
    v-else
    ref="gallery"
    v-slot="{ scrollerRef, fadeEdges }"
    tag="nav"
    class="jwx-widget-editor__gallery jwx-widget-editor-gallery"
    data-layout="row"
    :aria-label="t('widgets.editorGallery')"
    :previousLabel="t('widgets.showPreviousWidgets')"
    :nextLabel="t('widgets.showMoreWidgets')"
  >
    <ul :ref="scrollerRef" class="jwx-widget-editor-gallery__row" :data-fade="fadeEdges">
      <li v-for="widgetId of widgetStore.availableWidgets" :key="widgetId" :data-active="isActive(widgetId) || null">
        <button
          @click="handleSelect(widgetId)"
          type="button"
          class="jwx-button-control jwx-widget-editor-gallery__row-button"
          data-surface="1"
          data-shape="pill"
          :data-selected="isActive(widgetId)"
          :aria-current="isActive(widgetId) || null"
        >
          <Icon :name="widgetStore.getDefinition(widgetId).icon" class="jwx-button-control__icon" />
          {{ getTitle(widgetId) }}
        </button>
      </li>
    </ul>
  </ScrollAffordance>
</template>
